import { SmIcon } from '@smiles/smiles-react-design-system';
import React, { useEffect, useRef, useState } from 'react';
import { ErrorMessage, Tooltip } from '..';
import { Utils } from '../../utils';

import { useContextSelector } from '../../hooks';
import type { RentalType } from '../../types';

export interface ICalendarInputProps {
  id: string;
  label?: string;
  placeholder: string;
  placeholderIcon?: string;
  disabled?: boolean;
  type: RentalType;
  onCalendarToggle: (isOpen: boolean) => void;
}

export const CalendarInput = ({
  id,
  label,
  disabled = false,
  placeholder,
  placeholderIcon,
  type,
  onCalendarToggle,
  ...props
}: ICalendarInputProps) => {
  const {
    errors: { datesError },
    search: { dates, location, differentAgencies },
    generics: { isMobile },
    update,
    inputs,
  } = useContextSelector();

  const [inputTextValue, setInputTextValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (dates[type]) {
      const formattedDate = Utils.format.formatDate(dates[type] as string, 'DD/MM/YY');
      setInputTextValue(formattedDate);
    } else {
      setInputTextValue('');
    }
  }, [dates, type]);

  useEffect(() => {
    const input = inputRef.current as HTMLInputElement;

    if (inputs.datesModal && type === 'pickup') {
      input.focus();
      input.click();
    }
  }, [inputs]);

  const handleInputFocus = () => {
    const input = inputRef.current as HTMLInputElement;

    if (!isMobile) {
      update.errors.reset();

      const hasEmptyLocation = Utils.error.validateInputs(
        [['location', { [type]: location[type] }]],
        differentAgencies,
        update.errors,
      );

      if (!hasEmptyLocation) {
        onCalendarToggle(true);
        input.focus();
        input.classList.add('focused');
      }
    } else {
      onCalendarToggle(true);
      input.focus();
      input.classList.add('focused');
    }
  };

  const handleInputBlur = () => {
    const input = inputRef.current as HTMLInputElement;
    input.classList.remove('focused');
  };

  return (
    <section className="calendar-input-container" {...props}>
      {label && (
        <article className="calendar-input-label-wrapper">
          <label className="calendar-input-label" htmlFor={`calendar-input-field-${id}`}>
            {label}
          </label>

          {type === 'pickup' && (
            <Tooltip
              containerClassName="calendar-input-label-tooltip-container"
              tooltipClassName="calendar-input-label-tooltip"
              position="right"
              text="Antecedência mínima de 4h para locações nacionais e internacionais."
              offset={{ right: 4 }}
            >
              <SmIcon className="calendar-input-label-icon" name="info_outline" type="round" />
            </Tooltip>
          )}
        </article>
      )}

      <section className="calendar-input-field-wrapper">
        <input
          className={`calendar-input-field${datesError[type].active ? ' error' : ''}`}
          type="text"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          ref={inputRef}
          id={`calendar-input-field-${id}`}
          placeholder={placeholder}
          value={inputTextValue}
          disabled={isMobile && disabled}
          onFocus={handleInputFocus}
          onClick={handleInputFocus}
          onBlur={handleInputBlur}
          readOnly
        />

        {isMobile && placeholderIcon && (
          <SmIcon className="calendar-input-field-placeholder-icon" name={placeholderIcon} type="round" />
        )}
      </section>

      {!isMobile && <ErrorMessage isActive={datesError[type].active} message={datesError[type].message} />}
    </section>
  );
};
